/**
 * Anzeigeformate des Testmodus – pure Funktionen, damit Popup (F2), Preview (F3) und Toast dieselben
 * Texte erzeugen. Uhrzeiten immer in Europe/Berlin: „erneuert sich um 00:00 Uhr" meint deutsche Zeit,
 * auch wenn der Browser in einer anderen Zeitzone läuft.
 */

const BERLIN_TIME = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});

/** „10:42 Uhr" aus einem ISO-Zeitstempel; leerer String, wenn er fehlt oder ungültig ist. */
export function formatClockTime(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return '';
  }
  return `${BERLIN_TIME.format(date)} Uhr`;
}

/**
 * Reset-Zeit des Tageslimits aus `RuleTestQuota.resetsAt`, z. B. „00:00 Uhr".
 * Fällt auf „00:00 Uhr" zurück, solange die Quota noch nicht geladen ist.
 */
export function formatResetTime(resetsAt: string | null | undefined): string {
  return formatClockTime(resetsAt) || '00:00 Uhr';
}

/**
 * Tooltip am gesperrten „Testen"-Button: Überschrift und Text, oder `null`, solange Läufe übrig sind.
 *
 * Limit 0 setzt das Admin Center für ein einzelnes Konto; dafür gibt es kein „ab 00:00 Uhr wieder
 * verfügbar". Eine Stelle für Editoren und Popup, damit alle dasselbe sagen.
 */
export function ruleTestLimitTooltip(
  quota: {limit: number; remaining: number; resetsAt?: string | null} | null | undefined,
): {title: string; text: string} | null {
  if (!quota || quota.remaining > 0) {
    return null;
  }
  if (quota.limit <= 0) {
    return {
      title: 'Testläufe nicht freigeschaltet',
      text: 'Für dein Konto sind derzeit keine Testläufe freigeschaltet.',
    };
  }
  return {
    title: 'Tageslimit erreicht',
    text: `Du hast heute alle ${quota.limit} Testläufe genutzt. Ab ${formatResetTime(quota.resetsAt)} wieder verfügbar.`,
  };
}

/** Dauer als „m:ss" (Figma: „Dauer: 0:18", „Dauer 0:42"). Negative/fehlende Werte → „0:00". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !isFinite(ms) || ms < 0) {
    return '0:00';
  }
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Vergangene Zeit seit `startedAt` bis `now` – für die laufende Anzeige im Popup. */
export function elapsedMs(startedAt: string | null | undefined, now: number = Date.now()): number {
  if (!startedAt) {
    return 0;
  }
  const start = new Date(startedAt).getTime();
  return isNaN(start) ? 0 : Math.max(0, now - start);
}
